<template>
  <div class="queue-locations-container" data-testid="locations-container">
    <!-- QUEUE LOCATIONS HEADER -->
    <div class="d-flex flex-wrap justify-content-between gap-2">
      <div class="d-flex gap-2 my-auto">
        <h2 id="queue-locations-header" class="queue-header-text">
          Enable Queue for locations
        </h2>
        <HelpCenterButton
          use-securly-design-system
          content-key="queue_locations_v003"
        />
      </div>
      <hr class="queue-header-horizontal-line flex-grow-1 align-self-center" />
    </div>
    <!-- QUEUE LOCATIONS HEADER -->

    <!-- QUEUE LOCATIONS LIST CONTAINER -->
    <div class="queue-locations-list" :class="{ loading: isLoading }">
      <!-- LOCATION SEARCH -->
      <div
        class="d-flex flex-wrap align-items-center justify-content-between gap-2"
      >
        <div class="d-flex gap-2" :class="{ 'queue-loading': isInputDisabled }">
          <InputField
            id="search"
            prepend-icon="ri-search-line"
            placeholder="Search"
            v-model="search"
            @input="handleSearchInput"
          />
        </div>

        <div class="text-muted">
          Use toggle to choose locations that allow students to queue for a pass
        </div>
      </div>
      <!-- LOCATION SEARCH -->

      <!-- ERROR HANDLER -->
      <ErrorHandler :error="serverError" @done="serverError = null" />
      <!-- ERROR HANDLER -->

      <template v-if="!isInitialDataLoading">
        <!-- LOADING INDICATOR DURING REFRESH -->
        <div
          v-if="isLoading"
          class="justify-content-center align-content-center rounded-30 mt-6 d-flex gap-2"
        >
          <CSpinner color="primary" />
        </div>

        <!-- QUEUE LOCATIONS -->
        <LocationList
          v-else-if="hasLocations"
          :has-search="search.length > 0"
          :locations="locations"
          :has-more-pages="hasMorePages"
          :is-loading="isLoading"
          :is-loading-more="isLoadingMore"
          :are-all-location-queueable="areAllLocationQueueable"
          @change="onChange"
          @load-more="loadMore"
        />
        <!-- QUEUE LOCATIONS -->

        <!-- NO QUEUE LOCATIONS -->
        <div v-else class="text-center text-muted mt-5">
          No locations available
        </div>
        <!-- NO QUEUE LOCATIONS -->
      </template>

      <!-- LOCATIONS WHEN LOADING -->
      <div
        v-else
        class="justify-content-center align-content-center rounded-30 mt-6 d-flex gap-2"
      >
        <CSpinner color="primary" />
      </div>
      <!-- LOCATIONS WHEN LOADING -->
    </div>
    <!-- QUEUE LOCATIONS LIST CONTAINER -->
  </div>
</template>

<script>
import HelpCenterButton from "@/v3components/HelpCenterButton.vue"
import LocationList from "@/v3components/Queue/QueueLocation/LocationList.vue"
import InputField from "@/v3components/shared/Form/InputField.vue"
import ErrorHandler from "@/v3components/VisitorErrorHandler.vue"
import { computed, onMounted, onUnmounted, reactive, ref } from "vue"
import { useStore } from "vuex"

export default {
  name: "QueueLocationsContainer",
  components: {
    HelpCenterButton,
    InputField,
    LocationList,
    ErrorHandler
  },
  setup() {
    const store = useStore()
    const searchDebounceTimeout = ref(null)

    const state = reactive({
      data: { items: [], meta: {}, allLocationQueueable: false },
      loading: {
        initial: true,
        refresh: true,
        more: false
      },
      pagination: {
        currentPage: 1,
        perPage: 25
      },
      search: "",
      error: null
    })

    const locations = computed(() => {
      return state?.data?.items || []
    })

    const areAllLocationQueueable = computed({
      get: () => state?.data?.allLocationQueueable || false,
      set: (val) => {
        state.data.allLocationQueueable = val
      }
    })

    const hasLocations = computed(() => {
      return locations?.value?.length > 0
    })

    const isInputDisabled = computed(() => {
      return state?.loading?.refresh || state?.loading?.initial || false
    })

    const hasMorePages = computed(() => {
      return (
        Number(state?.pagination?.currentPage || 0) <
        Number(state?.data?.meta?.last_page || 0)
      )
    })

    const isLoading = computed(() => {
      return state?.loading?.refresh || false
    })

    const isLoadingMore = computed(() => {
      return state?.loading?.more || false
    })

    const isInitialDataLoading = computed(() => {
      return state?.loading?.initial || false
    })

    const serverError = computed({
      get: () => state?.error || null,
      set: (val) => {
        state.error = val
      }
    })

    const search = computed({
      get: () => state?.search || "",
      set: (val) => {
        state.search = val
      }
    })

    const resetPagination = () => {
      state.pagination.currentPage = 1
      state.data = { items: [], meta: {}, allLocationQueueable: false }
    }

    const handleSearchInput = () => {
      if (searchDebounceTimeout.value) {
        clearTimeout(searchDebounceTimeout.value)
      }

      searchDebounceTimeout.value = setTimeout(() => {
        state.error = null
        resetPagination()
        getQueueLocations()
      }, 500)
    }

    const getQueueLocations = async (loadMore = false) => {
      try {
        if (loadMore) {
          state.loading.more = true
        } else {
          state.loading.refresh = true
        }

        const queryParams = {
          currentPage: state.pagination.currentPage,
          perPage: state.pagination.perPage
        }

        if (state.search) {
          queryParams.search = state.search
        }

        const response = await store.dispatch(
          "queueSettings/getQueueLocations",
          queryParams
        )

        if (response && response.data) {
          if (loadMore) {
            state.data = {
              items: [
                ...(state?.data?.items || []),
                ...(response?.data?.items || [])
              ],
              meta: response?.data?.meta || {},
              allLocationQueueable:
                response?.data?.all_location_queueable || false
            }
          } else {
            state.data = {
              items: response?.data?.items || [] || [],
              meta: response.data.meta || {},
              allLocationQueueable:
                response?.data?.all_location_queueable || false
            }
          }
        }
      } catch (error) {
        state.error = error
      } finally {
        state.loading.refresh = false
        state.loading.more = false
      }
    }

    const loadMore = async () => {
      if (!hasMorePages.value || state.loading.refresh || state.loading.more) {
        return
      }

      state.error = null
      state.pagination.currentPage++
      await getQueueLocations(true)
    }

    const onChange = async ({ shouldEnableAll, location }) => {
      try {
        state.loading.refresh = true
        await store.dispatch("queueSettings/updateQueueLocation", {
          shouldEnableAll,
          location
        })
        state.error = null
        resetPagination()
        await getQueueLocations()
      } catch (error) {
        state.error = error
      } finally {
        state.loading.refresh = false
      }
    }

    onMounted(async () => {
      state.loading.initial = true
      await getQueueLocations()
      state.loading.initial = false
    })

    onUnmounted(() => {
      if (searchDebounceTimeout.value) {
        clearTimeout(searchDebounceTimeout.value)
      }
    })

    return {
      isLoading,
      isLoadingMore,
      isInitialDataLoading,
      isInputDisabled,
      hasLocations,
      hasMorePages,
      serverError,
      search,
      areAllLocationQueueable,
      locations,
      onChange,
      handleSearchInput,
      loadMore
    }
  }
}
</script>

<style scoped></style>
